import { Color, MeshStandardMaterial, Object3D, Vector3 } from "three";
import {
  defaultDoorHeight,
  defaultDoorWidth,
  defaultSideFrameWidth,
} from "./doorSceneConstants";
import { GLTF } from "three-stdlib";

export interface FramePositions {
  top: Vector3;
  bottom: Vector3;
  left: Vector3;
  right: Vector3;
}

export interface FrameScaling {
  horizontal: Vector3;
  vertical: Vector3;
}

export const PRODUCT_PAGE_SLUGS: Record<string, string> = {
  opdekdeurmetkozijn: "opdekdeur-met-kozijn",
  opdekdeur: "opdekdeur-in-bestaand-kozijn",
  stompedeur: "stompe-deur-in-bestaand-kozijn",
  schuifdeur: "schuifdeur",
  taatsdeur: "taatsdeur",
  paneel: "wandpaneel-enkelvoudig",
  scharnierdeur: "scharnierdeur-met-stomp-kozijn",
  paneelpaneel: "wandpaneel-tweevoudig",
  paneelpaneelpaneel: "wandpaneel-drievoudig",
  paneelpaneelpaneelpaneel: "wandpaneel-viervoudig",
  dubbel: "dubbele-taatsdeur",
  dubbel1paneel: "dubbele-taatsdeur-met-wand",
  dubbel2paneel: "wand-dubbele-taatsdeur-wand",
  taatsdeurtaatsdeurpaneelpaneel: "dubbele-taatsdeur-wand-wand",
  taatsdeurpaneelpaneel: "taatsdeur-wand-wand",
  taatsdeur2paneel: "wand-taatsdeur-wand",
  taatsdeur1paneel: "taatsdeur-wand",
  schuifdeurpaneelpaneel: "schuifdeur-wand-wand",
  schuifdeurpaneelpaneelschuifdeur: "schuifdeur-wand-wand-schuifdeur",
  schuifdeurschuifdeurpaneelpaneel: "dubbele-schuifdeur-wand-wand",
  schuifdeurpaneelschuifdeur: "",
  schuifpaneelschuiftrap: "schuifdeuren-met-wand-bij-trap",
  dubbeleschuifdeur1paneel: "dubbele-schuifdeur-met-wand",
  dubbeleschuifdeur2paneel: "wand-dubbele-schuifdeur-wand",
  dubbeleschuifdeur: "dubbele-schuifdeur",
  schuifdeur2paneel: "wand-schuifdeur-wand",
  schuifdeur1paneel: "schuifdeur-met-wand",
  scharnierdeur1paneel: "scharnierdeur-met-wand",
  scharnierdeur2paneel: "wand-scharnierdeur-wand",
  dubbelescharnierdeur: "dubbele-scharnierdeur-met-stomp-kozijn",
  dubbelescharnierdeur1paneel: "dubbele-scharnierdeur-wand",
  dubbelescharnierdeur2paneel: "wand-dubbele-scharnierdeur-wand",
  scharnierdeurpaneelscharnierdeur: "scharnierdeur-wand-scharnierdeur",
  scharnierpaneelscharniertrap: "scharnierdeuren-met-wand-bij-trap",
  scharnierdeurpaneelpaneel: "",
  scharnierdeurpaneelpaneelscharnierdeur: "",
  scharnierdeurscharnierdeurpaneelpaneel: "",
  kaderdeur: "enkele-kaderdeur-met-kozijn",
  dubbelekaderdeur: "dubbele-kaderdeur-met-kozijn",
  zakelijkkozijn: "deurkozijn-enkel",
};

export const getFramePositions = (
  width: number,
  height: number,
  reverse: boolean = false,
): FramePositions => {
  // Size of top and bottom is not the same as the whole door width
  // Door width = (side frame width * 2) + top / bottom frame
  const frameXPosition = getFrameXPosition(width);
  return {
    top: new Vector3(0, (height - defaultDoorHeight) / 1000, 0),
    bottom: new Vector3(0, 0, 0),
    left: new Vector3(reverse ? frameXPosition : -frameXPosition, 0, 0),
    right: new Vector3(reverse ? -frameXPosition : frameXPosition, 0, 0),
  };
};

export const getDoubleFramePositions = (
  width: number,
  height: number,
  side: "left" | "middle" | "right",
): FramePositions => {
  // Size of top and bottom is not the same as the whole door width
  // Door width = (side frame width * 2) + top / bottom frame
  const frameXPosition = getFrameXPosition(width) * 2;
  return {
    top: new Vector3(0, (height - defaultDoorHeight) / 1000, 0),
    bottom: new Vector3(0, 0, 0),
    left: new Vector3(
      side === "left" || side === "middle" ? frameXPosition : 0,
      0,
      0,
    ),
    right: new Vector3(
      side === "right" || side === "middle" ? -frameXPosition : 0,
      0,
      0,
    ),
  };
};

export const getKozijnFramePositions = (
  widths: number[],
  height: number,
  option: "single" | "double",
): FramePositions => {
  let rightPosition;
  let leftPosition;

  switch (option) {
    case "single":
      rightPosition = getFramePositions(widths[0], height);
      leftPosition = getFramePositions(widths[widths.length - 1], height);
      break;
    case "double":
      rightPosition = getDoubleFramePositions(widths[0], height, "middle");
      leftPosition = getDoubleFramePositions(
        widths[widths.length - 1],
        height,
        "middle",
      );
      break;
  }

  const left = (widths[0] - defaultDoorWidth) / 1000 / 2;
  const right = (widths[widths.length - 1] - defaultDoorWidth) / 1000 / 2;

  let topXOffset = 0;
  if (left > right) {
    topXOffset = -left + right;
  } else if (left < right) {
    topXOffset = -left + right;
  }

  return {
    top: new Vector3(topXOffset, (height - defaultDoorHeight) / 1000, 0),
    bottom: new Vector3(0, 0, 0),
    left: new Vector3(-leftPosition.right.x, 0, 0),
    right: new Vector3(-rightPosition.left.x, 0, 0),
  };
};

export const getFrameScaling = (
  width: number,
  height: number,
  widthMultiplier: number = 1,
): FrameScaling => {
  return {
    horizontal: new Vector3(width / (defaultDoorWidth * widthMultiplier), 1, 1),
    vertical: new Vector3(1, height / defaultDoorHeight, 1),
  };
};

export const getGlassPosition = (
  height: number,
  alternatePosition: boolean = true,
): Vector3 => {
  const y = alternatePosition ? -(25 / height) : 0;
  return new Vector3(0, y, 0);
};

export const getFrameXPosition = (width: number): number => {
  const widthDifference = width - defaultDoorWidth;

  // Check the difference in width between current width and default width.
  // Top and bottom scales from the middle, so the extra width is added between left and right side. Hence, the / 2 to get the width difference for one side
  // Then check the width difference against the default frame side width, to get an offset for the position of the side frame
  return (
    widthDifference / 1000 / 2 - widthDifference / defaultSideFrameWidth / 1000
  );
};

export const getProductPageSlug = (configuration: string): string => {
  if (configuration === "") return "";
  return PRODUCT_PAGE_SLUGS[configuration];
};

// Type of useLoader() results regarding the materials
export type GLTFMaterialResult = GLTF & {
  materials: {
    Helder: THREE.MeshPhysicalMaterial;
    Mat: THREE.MeshPhysicalMaterial;
    Rook: THREE.MeshPhysicalMaterial;
    ["Rook.mat"]: THREE.MeshPhysicalMaterial;
    Brons: THREE.MeshPhysicalMaterial;
    ["Brons.mat"]: THREE.MeshPhysicalMaterial;
    ["Helder.linnen"]: THREE.MeshPhysicalMaterial;
    ["Rook.linnen"]: THREE.MeshPhysicalMaterial;
    ["Brons.linnen"]: THREE.MeshPhysicalMaterial;
    PoedercoatZwartGrijs: THREE.MeshStandardMaterial;
    PoedercoatGitZwart: THREE.MeshStandardMaterial;
    PoedercoatZuiverWit: THREE.MeshStandardMaterial;
    PoedercoatKiezelGrijs: THREE.MeshStandardMaterial;
    PoedercoatOlijfGroen: THREE.MeshStandardMaterial;
    PoedercoatVensterGrijs: THREE.MeshStandardMaterial;
    PoedercoatGrijsBlauw: THREE.MeshStandardMaterial;
    PoedercoatGoud: THREE.MeshStandardMaterial;
    PoedercoatMahonie: THREE.MeshStandardMaterial;
    PoedercoatBrons: THREE.MeshStandardMaterial;
    PoedercoatLeem: THREE.MeshStandardMaterial;
    PoedercoatGrijsBruin: THREE.MeshStandardMaterial;
  };
};

// Type of glass materials
export type DoorGlassMaterial = {
  Helder: THREE.MeshPhysicalMaterial;
  Mat: THREE.MeshPhysicalMaterial;
  Rook: THREE.MeshPhysicalMaterial;
  ["Rook.mat"]: THREE.MeshPhysicalMaterial;
  Brons: THREE.MeshPhysicalMaterial;
  ["Brons.mat"]: THREE.MeshPhysicalMaterial;
  ["Helder.linnen"]: THREE.MeshPhysicalMaterial;
  ["Rook.linnen"]: THREE.MeshPhysicalMaterial;
  ["Brons.linnen"]: THREE.MeshPhysicalMaterial;
  PoedercoatZwartGrijs: THREE.MeshStandardMaterial;
  PoedercoatGitZwart: THREE.MeshStandardMaterial;
  PoedercoatZuiverWit: THREE.MeshStandardMaterial;
  PoedercoatKiezelGrijs: THREE.MeshStandardMaterial;
  PoedercoatOlijfGroen: THREE.MeshStandardMaterial;
  PoedercoatVensterGrijs: THREE.MeshStandardMaterial;
  PoedercoatGrijsBlauw: THREE.MeshStandardMaterial;
  PoedercoatGoud: THREE.MeshStandardMaterial;
  PoedercoatMahonie: THREE.MeshStandardMaterial;
  PoedercoatBrons: THREE.MeshStandardMaterial;
  PoedercoatLeem: THREE.MeshStandardMaterial;
  PoedercoatGrijsBruin: THREE.MeshStandardMaterial;

  [key: string]: THREE.MeshPhysicalMaterial | THREE.MeshStandardMaterial;
};

// General type of GLTF results
export type GLTFDoorResult = GLTF & {
  nodes: {
    [key: string]: THREE.Mesh | Object3D;
  };
};

// HARDCODED PART
// SOON REFACTORED TO BLENDER SOLUTION
const PoedercoatZwartGrijs = new Color("#555A57");
const PoedercoatGitZwart = new Color("#3D403E");
const PoedercoatZuiverWit = new Color("#FFFFFD");
const PoedercoatKiezelGrijs = new Color("#DBDBD4");
const PoedercoatOlijfGroen = new Color("#B5B5AA");
const PoedercoatVensterGrijs = new Color("#C6CCCE");
const PoedercoatGrijsBlauw = new Color("#3B4852");
const PoedercoatGoud = new Color("#C8AE77");
const PoedercoatMahonie = new Color("#79615F");
const PoedercoatBrons = new Color("#918775");
const PoedercoatLeem = new Color("#938E86");
const PoedercoatGrijsBruin = new Color("#5B5252");

// 4 new colors

// const PoedercoatDarkBronze = new Color("#54341e");
// const PoedercoatGrijsBeige = new Color("#b59f8a");
// const PoedercoatPearlWhite = new Color("#ede6d3");
// const PoedercoatTaupe = new Color("#B0A290");

export const adjustNewColors = (materials: any) => {
  materials["PoedercoatZwartGrijs"].color = PoedercoatZwartGrijs;
  materials["PoedercoatZwartGrijs"].metalness = 0.9;
  materials["PoedercoatGitZwart"].color = PoedercoatGitZwart;
  materials["PoedercoatZuiverWit"].color = PoedercoatZuiverWit;
  materials["PoedercoatZuiverWit"].metalness = 0;
  materials["PoedercoatKiezelGrijs"].color = PoedercoatKiezelGrijs;
  materials["PoedercoatOlijfGroen"].color = PoedercoatOlijfGroen;
  materials["PoedercoatVensterGrijs"].color = PoedercoatVensterGrijs;
  materials["PoedercoatGrijsBlauw"].color = PoedercoatGrijsBlauw;
  materials["PoedercoatGoud"].color = PoedercoatGoud;
  materials["PoedercoatMahonie"].color = PoedercoatMahonie;
  materials["PoedercoatBrons"].color = PoedercoatBrons;
  materials["PoedercoatLeem"].color = PoedercoatLeem;
  materials["PoedercoatGrijsBruin"].color = PoedercoatGrijsBruin;
  materials["PoedercoatGrijsBruin"].metalness = 0.95;

  materials["PoedercoatDarkBronze"].metalness = 0.75;
  materials["PoedercoatGrijsBeige"].metalness = 0.7;
  materials["PoedercoatPearlWhite"].metalness = 0.2;
  materials["PoedercoatTaupe"].metalness = 0.3;
};
